import './css/Inspector.css';
import { connect } from "react-redux";
import React from "react";
import { useState, useEffect } from 'react';
import * as d3 from 'd3';
import { divergingColorSchemes, properties, sequentialColorSchemes } from './Defs';
import { addToStack, getAggregator, getDefaultLayout, getPropertyDef, getReusableScales, getScaleType, getScheme } from './Util';
import { SketchPicker } from 'react-color';
import store from './store';
import { createNewItem, toggleAxisHandle, updateItem } from './canvasSlice';
import { Dropdown, Form, Stack } from 'react-bootstrap';

const mapStateToProps = state => ({
    selected: state.canvas.selected,
    directSelected: state.canvas.directSelected,
    itemUpdated: state.canvas.itemUpdated
});

const headerClasses = {};
for (let g in properties) {
    let classes = [];
    for (let prop in properties[g]) {
        for (let c of properties[g][prop].class) {
            if (classes.indexOf(c) < 0)
                classes.push(c);
        }
    }
    headerClasses[g] = classes;
}

const updatePropertyControl = (propDef, item, scene) => {

    d3.select("#" + propDef.id).style("padding-left", "3px");
    d3.select("#" + propDef.id + "ScaleBtn").style("display", "none");

    if (propDef.accessor) {
        let val = item;

        for (let a of propDef.accessor)
            val = val[a];

        if (propDef.type === "numeric")
            val = val.toFixed(1);

        if (propDef.id === "vxShape" && !val)
            val = "none";

        if (propDef.type === "color") {
            d3.select("#" + propDef.id).style("background", val).property("value", val);
        } else if (propDef.type === "boolean") {
            d3.select("#" + propDef.id).property("checked", val);    
        } else {
            d3.select("#" + propDef.id).property("value", val).property("disabled", propDef.channel === "area");
        }
    }

    //check if bound to data
    if (propDef.channel) {
        let enc;
        if (propDef.channel === "width" || propDef.channel === "height" || propDef.channel === "area" || propDef.channel === "radius") {
            enc = scene.sizeBound(item, propDef.channel);
        } else if (propDef.channel === "x" || propDef.channel === "y") {
            enc = scene.positionBound(item, propDef.channel);
        } else {
            enc = scene.getEncodingByItem(item, propDef.channel);
        }
        let encodable = item.type === "segment" ? item.parent.dataScope : item.type === "vertex" ? item.parent.dataScope || item.dataScope : item.dataScope;
        if (enc) {
            if (enc.channel === propDef.channel) {
                d3.select("#" + propDef.id).property("value", enc.field).property("disabled", true).style("pointer-events", "none");
                d3.select("#" + propDef.id + "UnencodeBtn").style("display", "inline-block")
                    .on("click", function(e){
                        scene.removeEncoding(enc);
                        let o = {};
                        o[enc.channel] = true;
                        store.dispatch(updateItem({id: item.id, type: item.type, properties: o}));
                    });
                d3.select("#" + propDef.id + "EncodeBtn").style("display", "none");
                let scaleCustomizable = (propDef.type === "numeric" && ["linear", "sqrt", "log", "power"].indexOf(enc.scale.type) >= 0) || 
                                            (propDef.type === "color" && ["sequentialColor", "divergingColor"].indexOf(enc.scale.type) >= 0);
                if (scaleCustomizable) {
                    d3.select("#" + propDef.id).style("padding-left", "23px");
                    d3.select("#" + propDef.id + "ScaleBtn").style("display", "inline-block");
                }
            } else {
                let info = (enc.anyItem.type === "vertex" || enc.anyItem.type === "segment")? "[see " + enc.anyItem.type + "]" : "[see " + enc.channel + "]";
                d3.select("#" + propDef.id).property("value", info).property("disabled", true).style("pointer-events", "none");
                d3.select("#" + propDef.id + "UnencodeBtn").style("display", "none");
                d3.select("#" + propDef.id + "EncodeBtn").style("display", "none");
            }
        } else if (encodable) {
            let disabled = (propDef.group === "Segment Position" || propDef.group === "Vertex Position");
            d3.select("#" + propDef.id).property("disabled", disabled).style("pointer-events", "all");
            d3.select("#" + propDef.id + "UnencodeBtn").style("display", "none");
            d3.select("#" + propDef.id + "EncodeBtn").style("display", "inline-block");
        } else {
            let disabled = (propDef.group === "Segment Position" || propDef.group === "Vertex Position");
            d3.select("#" + propDef.id).property("disabled", disabled).style("pointer-events", "all");
            d3.select("#" + propDef.id + "UnencodeBtn").style("display", "none");
            d3.select("#" + propDef.id + "EncodeBtn").style("display", "none");
        }
    }

    if (propDef.id === "layoutType") {
        const layout = item.layout ? item.layout.type : "none";
        d3.select("#" + propDef.id).property("value", layout);
        d3.selectAll(".layout-prop").style("display", "none");
        d3.selectAll("." + layout + "-prop").style("display", "inline-block");
        let options = d3.select("#"+propDef.id).selectAll("option");
        let ct = item.firstChild.type, na = [];
        if (ct === "rect") {
            na.push("packing");
            if (!scene.getEncodingByItem(item.firstChild, "area")) {
                na.push("treemap");
            }
        } else if (ct === "circle") {
            na.push("treemap");
        } else if (ct === "collection") {
            na = na.concat(["packing", "treemap"]);
        } //TODO: handle other item types
        
        options.property("disabled", function(d, i) {
            const v = d3.select(this).property("value");
            return na.indexOf(v) >= 0;
        })
        
        const params = propDef.options.filter(d => d.value === layout)[0].parameters;
        for (const p of params) {
            const paramDef = properties["Layout"][p];
            let v = item.layout[paramDef.accessor[0]];
            if (paramDef.type === "numeric-integer")
                v = v.toFixed(0);
            else if (paramDef.type === "numeric")
                v = v.toFixed(1);
            //handle layout parameter controls
            d3.select("#" + paramDef.id).property("value", v);
            d3.select("#" + paramDef.id + "ScaleBtn").style("display", "none");
        }
    }

    if (propDef.id === "childrenOrder") {
        d3.select("#" + propDef.id).selectAll("option").remove();
        if (item.dataScope) {
            let options = item.dataScope.dataTable.fields;
            d3.select("#" + propDef.id).selectAll("option").data(options).enter().append("option")
                .property("value", d => d).text(d => d === "atlas_rowId" ? "default" : d);
            d3.select("#" + propDef.id).property("value", item.childrenOrder.field);
        } //TODO: hide childrenOrder if item has no data scope
    }

    if (propDef.id === "axisOrientation") {
        let options = d3.select("#"+propDef.id).selectAll("option");
        if (item.channel === "x" || item.channel === "width") {
            options.property("disabled", function(d, i) {
                const v = d3.select(this).property("value");
                return v !== "top" && v !== "bottom";
            })
        } else if (item.channel === "y" || item.channel === "height") {
            options.property("disabled", function(d, i) {
                const v = d3.select(this).property("value");
                return v !== "left" && v !== "right";
            })
        } else {
            options.property("disabled", true);
        }
    }

    if (propDef.id === "includeZero") {
        if (item.channel !== "y") {
            d3.select("#" + propDef.id + "_row").style("display", "none");
        } else {
            d3.select("#" + propDef.id + "_row").style("display", "inline-block");
        }
    }

    if (propDef.id === "baseline") {
        let options = d3.select("#"+propDef.id).selectAll("option");
        let ori = item.orientation;
        if (ori === "horizontal") {
            options.property("disabled", function(d, i) {
                const v = d3.select(this).property("value");
                return v !== "top" && v!== "middle" && v !== "bottom";
            })
        } else if (ori === "vertical") {
            options.property("disabled", function(d, i) {
                const v = d3.select(this).property("value");
                return v !== "left" && v!== "center" && v !== "right";
            })
        } else {
            options.property("disabled", true);
        }
    }

    if (propDef.id === "vxShape") {
        vertexShapeUpdated();
    }
}

const vertexShapeUpdated = () => {
    let v = d3.select("#vxShape").property("value");
    if (v === "rect") {
        d3.select("#vxRadius_row").style("display", "none");
        d3.select("#vxWidth_row").style("display", "inline-block");
        d3.select("#vxHeight_row").style("display", "inline-block");
    } else if (v === "circle") {
        d3.select("#vxRadius_row").style("display", "inline-block");
        d3.select("#vxWidth_row").style("display", "none");
        d3.select("#vxHeight_row").style("display", "none");
    } else {
        d3.select("#vxRadius_row").style("display", "none");
        d3.select("#vxWidth_row").style("display", "none");
        d3.select("#vxHeight_row").style("display", "none");
    }
}

function Inspector(props) {
    // const dispatch = useDispatch();
    const [currentColor, setCurrentColor] = useState("#fff");
    const [currentColorPropDef, setCurrentColorPropDef] = useState(null);
    const [currentDataTable, setCurrentDataTable] = useState(null);
    const [pastLayouts, setPastLayouts] = useState({});

    useEffect(() => {
        setPastLayouts({});
    }, [props.scene]);

    useEffect(() => {
        if (currentColorPropDef === null) {
            d3.select("#colorPicker").style("display", "none");
            return;
        }
        let item = currentColorPropDef.id === "canvasFillColor" ? props.scene : 
                    props.selected.length > 0 ? props.scene.getItem(props.selected[0]) : 
                        props.directSelected ? props.scene.getItem(props.directSelected) : null;
        if (item === null) return;
        let c = item[currentColorPropDef.accessor];
        setCurrentColor(c ? c : "#fff");
        d3.select("#colorPicker").style("display", "block");
    }, [currentColorPropDef, props.scene, props.selected, props.directSelected]);

    useEffect(() => {
        if (currentColorPropDef === null) {
            return;
        }
        d3.select("#" + currentColorPropDef.id).style("background", currentColor).property("value", currentColor);
        if (currentColorPropDef.id === "canvasFillColor") {
            props.scene[currentColorPropDef.accessor] = currentColor;
        } else if (props.selected.length > 0) {
            let item = props.scene.getItem(props.selected[0]);
            if (item.type === "axis" || item.type === "gridlines" || item.type === "legend") {
                item[currentColorPropDef.accessor] = currentColor;
            } else {
                let prop = {};
                prop[currentColorPropDef.accessor] = currentColor;
                props.scene.setProperties(item, prop);
            }
        } else if (props.directSelected) {
            let item = props.scene.getItem(props.directSelected);
            let prop = {};
            prop[currentColorPropDef.accessor] = currentColor;
            props.scene.setProperties(item, prop);
        }
        props.renderer.render(props.scene);
    }, [currentColor, currentColorPropDef, props.scene, props.selected, props.directSelected, props.renderer]);

    useEffect(() => {
        if (!props.itemUpdated.id || props.itemUpdated.source === "inspector") return;
        const item = props.scene.getItem(props.itemUpdated.id);
        if (item === undefined) return;
        for (let prop in props.itemUpdated.properties) {
            const def = getPropertyDef(item.type, prop);
            if (def)
                updatePropertyControl(def, item, props.scene);
        }
    }, [props.scene, props.itemUpdated])

    useEffect(() => {
        let type, selected = props.selected.length > 0 ? props.scene.getItem(props.selected[0]) : undefined;
        if (!selected) {
            type = "canvas";
            selected = props.scene;
            setCurrentDataTable(null);
        } else {
            //selected = props.scene.getItem(props.selected[0]);
            type = selected.type;
            let item = props.scene.getItem(props.selected[0]);
            if (item.dataScope)
                setCurrentDataTable(item.dataScope.dataTable);
            else
                setCurrentDataTable(null);
        }
        let panel = d3.select(".propertyPanel");
        panel.selectAll(".propertyGroupHeader").style("display", "none");
        panel.selectAll(".propertyRow").style("display", "none");
        //if (props.selected.length === 1) {
        if (selected) {
            panel.selectAll("." + type + "-prop").style("display", "inline-block");
            for (let g in properties) {
                for (let prop in properties[g]) {
                    if (properties[g][prop].class.indexOf(type + "-prop") >= 0)
                        updatePropertyControl(properties[g][prop], selected, props.scene);
                }
            }
        }
    }, [props.scene, props.selected]);

    useEffect(() => {
        if (!props.directSelected) {
            setCurrentDataTable(null);
        } else {
            let item = props.scene.getItem(props.directSelected);
            if (item.dataScope)
                setCurrentDataTable(item.dataScope.dataTable);
            else if (item.parent.dataScope)
                setCurrentDataTable(item.parent.dataScope.dataTable);
            else
                setCurrentDataTable(null);
        }
        let panel = d3.select(".propertyPanel");
        panel.selectAll(".propertyGroupHeader").style("display", "none");
        panel.selectAll(".propertyRow").style("display", "none");
        if (props.directSelected) {
            let ds = props.scene.getItem(props.directSelected), type = ds.type;
            panel.selectAll("." + type + "-prop").style("display", "inline-block");
            for (let g in properties) {
                for (let prop in properties[g]) {
                    if (properties[g][prop].class.indexOf(type + "-prop") >= 0)
                        updatePropertyControl(properties[g][prop], ds, props.scene);
                }
            }
        }
    }, [props.scene, props.directSelected]);

    const setCategoricalValue = (attr) => {
        let ctrl = d3.select("#"+attr.id);
        if (ctrl.empty()) return;
        let val = ctrl.property("value");
        let item = props.selected.length > 0 ? props.scene.getItem(props.selected[0]) : props.scene.getItem(props.directSelected); 
        if (item === undefined) return;
        addToStack(props.scene, props.datasets, props.undoStack);
        const o = {};
        //TODO: handle layout properties in o properly for dispatch
        switch (attr.id) {
            case "axisOrientation":
            case "legendOrientation":
                item[attr.accessor[0]] = val;
                o[attr.accessor[0]] = val;
                break;
            case "orientation":
            case "horzGravity":
            case "vertGravity":
            case "direction":
                o[attr.accessor[0]] = val;
                props.scene.setProperties(item.layout, o);
                //props.scene.reCreateRelatedAxes(item);
                break;
            case "layoutType":
                o["layout"] = val;
                let l;
                if (!(item.id in pastLayouts))
                    pastLayouts[item.id] = [];
                pastLayouts[item.id].unshift(item.layout);
                if (pastLayouts[item.id].length > 0) {
                    l = pastLayouts[item.id].find(d => d && d.type === val);
                }
                if (!l)
                    l = getDefaultLayout(val);
                props.scene.setProperties(item, {"layout": l});
                updatePropertyControl(attr, item, props.scene);
                break;
            case "vxShape":
                o[attr.accessor[0]] = val;
                props.scene.setProperties(item, o);
                vertexShapeUpdated();
                break;
            case "childrenOrder":
                let dir = d3.select("#childrenOrderDirBtn").property("checked") ? "desc" : "asc";
                o[attr.accessor[0]] = {"field": val, "direction": dir};
                props.scene.setProperties(item, o);
                //props.scene.reCreateRelatedAxes(item);
                break;
            default:
                o[attr.accessor[0]] = val;
                props.scene.setProperties(item, o);
                break;
        }
        store.dispatch(updateItem({id: item.id, type: item.type, properties: o, source: "inspector"}));
    }

    const setBooleanValue = (attr) => {
        let ctrl = d3.select("#"+attr.id);
        if (ctrl.empty()) return;
        addToStack(props.scene, props.datasets, props.undoStack);
        let val = ctrl.property("checked");
        let item = props.selected.length > 0 ? props.scene.getItem(props.selected[0]) : props.scene.getItem(props.directSelected); 
        item[attr.accessor[0]] = val;
        const o = {};
        o[attr.accessor[0]] = val;
        store.dispatch(updateItem({id: item.id, type: item.type, properties: o, source: "inspector"}));
    }

    const setStringValue = (attr) => {
        let ctrl = d3.select("#"+attr.id);
        if (ctrl.empty()) return;
        let val = ctrl.property("value");
        let item = props.selected.length > 0 ? props.scene.getItem(props.selected[0]) : props.scene.getItem(props.directSelected); 
        addToStack(props.scene, props.datasets, props.undoStack);
        const o = {};
        switch (attr.id) {
            case "labelFormat":
                o[attr.accessor[0]] = val;
                item[attr.accessor[0]] = val;
                break;
            default:
                break;
        }
        store.dispatch(updateItem({id: item.id, type: item.type, properties: o, source: "inspector"}));
    }

    const setNumericValue = (attr) => {
        let ctrl = d3.select("#"+attr.id);
        if (ctrl.empty()) return;
        let val = parseFloat(ctrl.property("value"));
        if (isNaN(val)) return;
        if (attr.id !== "rowGap" && attr.id !== "colGap" && attr.id !== "labelRotation" && val < 0) return;
        if (attr.type === "numeric-integer") {
            val = parseInt(val);
        }
        let item = props.selected.length > 0 ? props.scene.getItem(props.selected[0]) : props.scene.getItem(props.directSelected); 
        addToStack(props.scene, props.datasets, props.undoStack);
        const o = {};
        switch (attr.id) {
            case "strokeWidth":
            case "width":
            case "height":
            case "area":
            case "fontSize":
            case "vxWidth":
            case "vxHeight":
            case "vxRadius":
            case "vxOpacity":
            case "vxStrokeWidth":
            case "opacity":
            case "radius":
            case "innerRadius":
            case "outerRadius":
                o[attr.channel] = val;
                props.scene.setProperties(item, o);
                break;
            case "rowGap":
            case "colGap":
            case "numRows":
            case "numCols":
                if (item.layout) {
                    o[attr.id] = val;
                    props.scene.setProperties(item.layout, o);
                }
                break;
            case "tickOffset":
            case "labelOffset":
            case "labelRotation":
            case "tickSize":
            case "pathPos":
            case "gridlineStrokeWidth":
                o[attr.accessor[0]] = val;
                item[attr.accessor[0]] = val;
                break;
            case "x":
            case "collectionX":
            case "glyphX":
                props.scene.translate(item, val - item.x, 0);
                break;
            case "y":
            case "collectionY":
            case "glyphY":
                props.scene.translate(item, 0, val - item.y);
                break;
            default:
                break;
        }
        store.dispatch(updateItem({id: item.id, type: item.type, properties: o, source: "inspector"}));
    }

    const encode = (f, c, scale) => {
        let item;
        if (props.selected.length >= 1) {
            item = props.scene.getItem(props.selected[0]);
        } else if (props.directSelected) {
            item = props.scene.getItem(props.directSelected);
        }

        if (!item) return;
        addToStack(props.scene, props.datasets, props.undoStack);
        // if ((c === "x" || c === "y") && item.type !== "area" && item.parent.type === "collection" && item.parent.layout) {
        //     props.scene.setProperties(item.parent, {"layout": undefined});
        // }
        if ((c === "x" || c === "y") && item.parent.type === "collection" && item.parent.layout) {
            switch (item.parent.layout.type) {
                case "grid":
                    if (c === "x" && item.parent.layout.numCols > 1)
                        props.scene.setProperties(item.parent, {"layout": undefined});
                    else if (c === "y" && item.parent.layout.numRows > 1)
                        props.scene.setProperties(item.parent, {"layout": undefined});
                    break;
                default:
                    props.scene.setProperties(item.parent, {"layout": undefined});
                    break;
            }
        }
        props.scene.encode(item, {field: f, channel: c, scale: scale});
        let guide;
        if (!scale) {
            if (c.indexOf("Color") > 0) {
                guide = props.scene.legend(c, f);
            } else if (c === "width" || c === "height" || c === "x" || c === "y") {
                guide = props.scene.axis(c, f, {item: item});
            }
        } else { //if merging scale, generate a new axis only when binding to line/path vertex, and the line/path orientation is orthogonal to the channel 
            if (item.type === "vertex" && ["path", "line"].indexOf(item.parent.type) >= 0 ) {
                let p = item.parent;
                let r = Math.abs((p.firstVertex.y - p.lastVertex.y + 1)/(p.firstVertex.x - p.lastVertex.x + 1));
                if ((r < 1 && c === "y") || (r > 1 && c === "x")) {
                    guide = props.scene.axis(c, f, {item: item});
                }
            }
        }
        props.scene.reCreateRelatedAxes(item);
        
        const o = {};
        o[c] = "encoded";
        store.dispatch(updateItem({id: item.id, type: item.type, properties: o}));
        if (guide)
            store.dispatch(createNewItem(guide.id));
    }

    const highlightAxis = (scale, show) => {
        for (let c of props.scene.children) {
            if (c.type === "axis" && c.encoding) {
                if (c.encoding.scale === scale) {
                    store.dispatch(toggleAxisHandle({axis: c.id, visible: show}));
                    return;
                }
            }
        }
    }

    const createNumericControl = (attr) => {
        return <input id={attr.id} onChange={(e) => setNumericValue(attr)}></input>;
        //return <div><button className="scaleBtn" id={attr.id+"ScaleBtn"}></button><input id={attr.id} onChange={(e) => setNumericValue(attr)}></input></div>
    }

    const createStringControl = (attr) => {
        return <input type="text" id={attr.id} onInput={(e) => setStringValue(attr)}></input>
    }

    const createColorControl = (attr) => {
        return <input id={attr.id} onClick={(e) => {setCurrentColorPropDef(attr);e.stopPropagation();}}></input>
    }

    const createCategoryControl = (attr) => {
        return <select id={attr.id} onChange={(e) => setCategoricalValue(attr)}>{attr.options.map( o => <option value={o.value} key={o.display}>{o.display}</option>)}</select>
    }

    const createRangeControl = (attr) => {
        return (
            <div className="propertySliderDiv">
                <input type="range" min={attr.range[0]} max={attr.range[1]} step={(attr.range[1] - attr.range[0])/10} className="propertySlider" id={attr.id} onChange={(e) => setNumericValue(attr)}></input>
                <div className="propertySliderMarks"> 
                    <div style={{margin: 0, float: "left"}}>{attr.range[0]}</div>
                    <div style={{margin: 0, float: "right"}}>{attr.range[1]}</div>
                </div>
            </div>
        )
    }

    const createBooleanControl = (propDef) => {
        return (
            <input type="checkbox" id={propDef.id} onChange={(e) => setBooleanValue(propDef)}></input>
        )
    }

    const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
        <button className='dropdown-item dropdown-toggle encodeFields' href="" ref={ref}
            // onMouseEnter={(e) => onClick(e)}
            onClick={(e) => {
                e.preventDefault();
                onClick(e);
            }}
        >
          {"  " + children}
        </button>
    ));

    const changeAggregator = (channel, aggr) => {
        let item;
        if (props.selected.length >= 1) {
            item = props.scene.getItem(props.selected[0]);
        } else if (props.directSelected) {
            item = props.scene.getItem(props.directSelected);
        }

        if (!item) return;
        addToStack(props.scene, props.datasets, props.undoStack);
        let enc = props.scene.getEncodingByItem(item, channel);
        if (!enc) return;
        enc.aggregator = aggr;
        props.scene.reCreateRelatedAxes(item);
        
        const o = {};
        o[channel] = "aggregator";
        store.dispatch(updateItem({id: item.id, type: item.type, properties: o}));
    }

    const changeScaleType = (channel, type) => {
        let item;
        if (props.selected.length >= 1) {
            item = props.scene.getItem(props.selected[0]);
        } else if (props.directSelected) {
            item = props.scene.getItem(props.directSelected);
        }

        if (!item) return;
        addToStack(props.scene, props.datasets, props.undoStack);
        let enc = props.scene.getEncodingByItem(item, channel);
        if (!enc) return;
        enc.scale.type = type;
        props.scene.reCreateRelatedAxes(item);
        
        const o = {};
        o[channel] = "scaleType";
        store.dispatch(updateItem({id: item.id, type: item.type, properties: o}));
    }

    const changeScheme = (channel, scheme) => {
        let item;
        if (props.selected.length >= 1) {
            item = props.scene.getItem(props.selected[0]);
        } else if (props.directSelected) {
            item = props.scene.getItem(props.directSelected);
        }

        if (!item) return;
        addToStack(props.scene, props.datasets, props.undoStack);
        let enc = props.scene.getEncodingByItem(item, channel);
        if (!enc) return;
        enc.scale.scheme = scheme;
        props.scene.reCreateRelatedLegends(item);
        const o = {};
        o[channel] = "scheme";
        store.dispatch(updateItem({id: item.id, type: item.type, properties: o}));
    };

    const toggleOrder = (e, propDef) => {
        let dir = e.target.checked ? "desc" : "asc";
        d3.select("#childrenOrderDirBtnImg").attr("src", "/app/icons/" + dir + ".png");
        addToStack(props.scene, props.datasets, props.undoStack);
        let item = props.scene.getItem(props.selected[0]); 
        const o = {};
        o[propDef.accessor[0]] = {field: item[propDef.accessor[0]].field, direction: dir};
        props.scene.setProperties(item, o);
        //props.scene.reCreateRelatedAxes(item);
        store.dispatch(updateItem({id: item.id, type: item.type, properties: o, source: "inspector"}));
        // let o = {};
        // o[enc.channel] = true;
        // store.dispatch(updateItem({id: item.id, type: item.type, properties: o}));
    };


    const createPropertyControl = (prop, propDef) => {
        let classes = "propertyRow " + propDef.class.join(" ");
        return (
        <tr className={classes} key={prop} id={propDef.id+"_row"}>
            <td className= "propertyName">{prop}</td>
            <td className="propertyInput">
            <Dropdown id={propDef.id+"ScaleBtn"} className="scaleBtnDiv">
                <Dropdown.Toggle className="scaleBtn" variant="link" bsPrefix="p-0">
                </Dropdown.Toggle>
                <Dropdown.Menu className="scaleMenu">
                    <div>
                    <Form>
                        <Form.Group style={{"margin": "0 10px 0 10px", "display": prop.indexOf("Color") < 0 ? "block" : "none"}}>
                            <Stack direction="horizontal" gap={1}>
                                <Form.Label className="scaleMenuLabel">Scale type</Form.Label>
                                <Form.Select value={getScaleType(propDef.channel, props.selected, props.directSelected, props.scene) ? getScaleType(propDef.channel, props.selected, props.directSelected, props.scene) : ""} onChange={(e) => changeScaleType(propDef.channel, e.target.value)} size="sm">
                                    <option value="linear">Linear</option>
                                    <option value="log">Log</option>
                                    <option value="sqrt">Square Root</option>
                                    <option value="power">Power (2)</option>
                                </Form.Select>
                            </Stack>
                        </Form.Group>
                        <Form.Group style={{"margin": "0 10px 0 10px", "display": getAggregator(propDef.channel, props.selected, props.directSelected, props.scene) ? "block" : "none"}}>
                            <Stack direction="horizontal" gap={1}>
                                <Form.Label className="scaleMenuLabel">Aggregator</Form.Label>
                                <Form.Select value={getAggregator(propDef.channel, props.selected, props.directSelected, props.scene) ? getAggregator(propDef.channel, props.selected, props.directSelected, props.scene) : ""} onChange={(e) => changeAggregator(propDef.channel, e.target.value)} size="sm">
                                    <option value="sum">Sum</option>
                                    <option value="mean">Average</option>
                                    <option value="max">Maximum</option>
                                    <option value="min">Minimum</option>
                                    <option value="median">Median</option>
                                    <option value="count">Count</option>
                                </Form.Select>
                            </Stack>
                        </Form.Group>
                        <Form.Group style={{"margin": "0 10px 0 10px", "display": prop.indexOf("Color") > 0 ? "block" : "none"}}>
                            <Stack direction="horizontal" gap={1}>
                                <Form.Label className="scaleMenuLabel">Color Scheme</Form.Label>
                                <Form.Select value={getScheme(propDef.channel, props.selected, props.directSelected, props.scene) ? getScheme(propDef.channel, props.selected, props.directSelected, props.scene) : ""} onChange={(e) => changeScheme(propDef.channel, e.target.value)} size="sm">
                                    {getScaleType(propDef.channel, props.selected, props.directSelected, props.scene) === "divergingColor" ? 
                                        Object.keys(divergingColorSchemes).map(o => <option value={o} key={o}>{divergingColorSchemes[o]}</option>) :
                                        Object.keys(sequentialColorSchemes).map(o => <option value={o} key={o}>{sequentialColorSchemes[o]}</option>) 
                                    }
                                </Form.Select>
                            </Stack>
                        </Form.Group>
                    </Form>
                    </div>
                </Dropdown.Menu>
            </Dropdown>
            {(function(){
                switch(propDef.type) {
                    case "numeric":
                    case "numeric-integer":
                        return createNumericControl(propDef);
                    case "numeric-range":
                        return createRangeControl(propDef);
                    case "color":
                        return createColorControl(propDef);
                    case "categorical":
                        return createCategoryControl(propDef);
                    case "boolean":
                        return createBooleanControl(propDef);
                    case "string":
                        return createStringControl(propDef);
                    default:
                        return createNumericControl(propDef);
                }
            })()}
            </td>
            {propDef.encodable ? (<td>
                <button className="unencodeBtn" id={propDef.id+"UnencodeBtn"}></button>
                <Dropdown id={propDef.id+"EncodeBtn"}>
                    <Dropdown.Toggle className="encodeBtn" variant="link" bsPrefix="p-0">
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {currentDataTable === null ? []: 
                            currentDataTable.getEncodableFields(propDef.channel).map((f, i) => {
                                let scales = getReusableScales(currentDataTable.getFieldType(f), propDef.channel, props.scene);
                                if (Object.keys(scales).length === 0) {
                                    return (
                                        <Dropdown.Item className="encodeFields" key={f}
                                            onClick={(e) => {encode(f, propDef.channel)}}>
                                            {f}
                                        </Dropdown.Item> 
                                    )
                                } else {
                                    let options = [];
                                    for (let k in scales) {
                                        options.push({fields: [...new Set(scales[k].map(d => d.field))].join("/"), scale: scales[k][0].scale})
                                    }
                                    return (
                                        // <Dropdown.Item onMouseEnter={(e) => {console.log(f, propDef.channel)}} className="encodeFields" key={f} eventKey={f}>{f}</Dropdown.Item>
                                        <Dropdown id={propDef.id+"_scale_"+f} key={propDef.id+"_scale_"+f} drop='start'>
                                            <Dropdown.Toggle as={CustomToggle} bsPrefix="p-0">
                                                {f}
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                {[null].concat(options).map((enc, i) => {
                                                    if (enc === null) {
                                                        return (
                                                        <Dropdown.Item className="encodeFields" key={"new"+i}
                                                            onClick={(e) => {encode(f, propDef.channel)}}
                                                        >
                                                        Create new scale
                                                        </Dropdown.Item>);
                                                    } else {
                                                        return (
                                                        <Dropdown.Item className="encodeFields" key={enc.scale.id+"_"+i}
                                                            onClick={(e) => {encode(f, propDef.channel, enc.scale)}}
                                                            onMouseEnter={(e) => {highlightAxis(enc.scale, true)}}
                                                            onMouseLeave={(e) => {highlightAxis(enc.scale, false)}}
                                                        >
                                                        {"Merge with " + enc.fields}
                                                        </Dropdown.Item>);
                                                    }
                                                })}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    )
                                }
                            }
                            )}
                    </Dropdown.Menu>
                </Dropdown>
                </td>) :
             propDef.id === "childrenOrder" ? (<td><label htmlFor="childrenOrderDirBtn"><img id="childrenOrderDirBtnImg" src="/app/icons/asc.png" className="orderDirBtn" alt="ascending"/></label>
                                                <input type="checkbox" id="childrenOrderDirBtn" style={{"display": "none"}} onChange={(e) => toggleOrder(e, propDef)}></input></td>) : <td></td>
            }
        </tr>)
    }

    return (
        <div>
            {/* <div className="rightPanel" onClick={(e) => setCurrentColorPropDef(null)}> */}
                <div className="propertyPanel" onClick={(e) => setCurrentColorPropDef(null)} style={{"overflow": "visible"}}>
                    {Object.keys(properties).map(g => 
                    <div key={g}>
                        <span className={headerClasses[g].concat(["propertyGroupHeader"]).join(" ")}>{g}</span>
                        <table>
                            <tbody>
                            {Object.keys(properties[g]).map(p => createPropertyControl(p, properties[g][p]))}
                            </tbody>
                        </table>
                    </div>)}
                </div>
            {/* </div> */}
            <div id="colorPicker">
                <SketchPicker disableAlpha={true} color={currentColor} onChangeComplete={e => setCurrentColor(e.hex)}/>
            </div>
        </div>
    )

}

export default connect(mapStateToProps)(Inspector);